import { apiFetch } from '@/lib/api-client';

import { customerEligibility } from './customer-eligibility';
import {
  campaignDetailSchema,
  campaignSummarySchema,
  type CampaignDetail,
  type CampaignOutcomeStatus,
  type CampaignSummary,
} from './outcomes/campaign.schema';

import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';

/**
 * Campaigns API client (SCRUM-531). Persists a campaign + its targeted customers when the
 * wizard sends it, so the outcome-tracking surface (SCRUM-71–75) has a durable record.
 *
 * BEST-EFFORT by design: the wizard's CSV/clipboard export is the officer's primary artifact
 * and must never be blocked by a persist failure (offline preview, transient API error). The
 * caller downloads the CSV FIRST, then fires this; failures are swallowed + logged.
 */
const CAMPAIGNS_URL = '/api/campaigns';

interface CampaignTargetPayload {
  customerId: string;
  matchMet: number;
  matchTotal: number;
}

interface PersistCampaignPayload {
  name: string;
  productId: string | null;
  targets: CampaignTargetPayload[];
}

/**
 * Persists a campaign from the wizard's resolved targets. The eligibility match count is
 * computed here (met/total) from the shared predicate and stored per target so the historical
 * count is stable even if the product's rules change later.
 *
 * Best-effort: never throws. Returns the persisted campaign id on success so the caller can
 * open the outcome-tracking detail view for the just-created campaign (SCRUM-72/73, D2), or
 * `null` on any failure (offline preview, transient API error) — the CSV export is the primary
 * artifact and must not be blocked.
 */
export async function persistCampaign(
  name: string,
  product: Product | null,
  targets: readonly Customer[],
): Promise<string | null> {
  const payload: PersistCampaignPayload = {
    name: name.trim() || 'Untitled campaign',
    productId: product?.id ?? null,
    targets: targets.map((c) => {
      const elig = product ? customerEligibility(c, product) : { met: 0, total: 0 };
      return { customerId: c.id, matchMet: elig.met, matchTotal: elig.total };
    }),
  };

  try {
    const res = await apiFetch(CAMPAIGNS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      // Non-2xx: the export already succeeded; persistence is a best-effort side-effect.
      console.warn(`Campaign persist returned HTTP ${String(res.status)} — export unaffected.`);
      return null;
    }
    const body: unknown = await res.json();
    const id = (body as { id?: unknown }).id;
    return typeof id === 'string' ? id : null;
  } catch (err) {
    console.warn('Campaign persist failed (offline or API error) — export unaffected.', err);
    return null;
  }
}

/**
 * Lists persisted campaigns for the selector (SCRUM-71 — GET /api/campaigns). Propagates
 * failures to the caller (like {@link getCampaign}) so the selector's query can surface an
 * error/empty state; it backs a deliberate render, not a fire-and-forget side effect. The
 * consuming hook MUST be token-gated (`enabled: accessToken !== null`) — an authed /api/*
 * read fired before the in-memory Bearer arrives caches a dead 401 for the session under the
 * global retry:false/staleTime:Infinity config (GOTCHA 5fe78eb9; why the prior list hook was
 * deleted in PR #246).
 */
export async function listCampaigns(): Promise<CampaignSummary[]> {
  const res = await apiFetch(CAMPAIGNS_URL);
  if (!res.ok) {
    throw new Error(`GET ${CAMPAIGNS_URL} failed: ${String(res.status)}.`);
  }
  const body: unknown = await res.json();
  return campaignSummarySchema.array().parse(body);
}

/**
 * Fetches one campaign with its per-customer outcome rows (GET /api/campaigns/{id}). Unlike
 * {@link persistCampaign}, this read propagates failures to the caller so the detail view can
 * surface a loading/error state (it backs a deliberate navigation, not a fire-and-forget side
 * effect).
 */
export async function getCampaign(campaignId: string): Promise<CampaignDetail> {
  const res = await apiFetch(`${CAMPAIGNS_URL}/${campaignId}`);
  if (!res.ok) {
    throw new Error(`Failed to load campaign ${campaignId} (HTTP ${String(res.status)}).`);
  }
  const body: unknown = await res.json();
  return campaignDetailSchema.parse(body);
}

/**
 * Records an outcome status for one customer in a campaign (SCRUM-74 —
 * PATCH /api/campaigns/{id}/outcomes/{customerId}). Throws on non-2xx so the caller can roll
 * back its optimistic update (the summary bar + row must never show a status the server
 * rejected).
 */
export async function updateOutcome(
  campaignId: string,
  customerId: string,
  status: CampaignOutcomeStatus,
): Promise<void> {
  const res = await apiFetch(`${CAMPAIGNS_URL}/${campaignId}/outcomes/${customerId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) {
    throw new Error(`Failed to update outcome (HTTP ${String(res.status)}).`);
  }
}

/**
 * Permanently deletes a campaign (SCRUM-813 — DELETE /api/campaigns/{id}). Irreversible: the
 * server cascades away the campaign's outcome tracking and outreach history. Throws on non-2xx
 * so the confirmation dialog can surface an error and NOT navigate away (a 403 here means the
 * caller is not a Bank Executive — the server is the authority, the client button is parity).
 * Bank-Executive-only is enforced server-side by the CampaignAdmin authorization policy.
 */
export async function deleteCampaign(campaignId: string): Promise<void> {
  const res = await apiFetch(`${CAMPAIGNS_URL}/${campaignId}`, { method: 'DELETE' });
  if (!res.ok) {
    throw new Error(`Failed to delete campaign ${campaignId} (HTTP ${String(res.status)}).`);
  }
}
